import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { supabase } from "../../supaBaseClient"; // ✅ Supabase import
import { API_BASE } from "../../config";
import Footer from "../homepage/footer";
import { openPdfInNewTab, preparePdfWindow } from "../../utils/pdfOpener";

// use centralized API_BASE from config

const Higher = () => {
  // State variables
  const [higherTopics, setHigherTopics] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedGrades, setCollapsedGrades] = useState({});
  const videoRef = useRef(null);
  const navigate = useNavigate();

  // ✅ Supabase Auth: detect and store logged-in user
  useEffect(() => {
    let unsub = () => {};

    (async () => {
      const { data, error } = await supabase.auth.getUser();
      if (!error && data?.user) {
        setUserId(data.user.id);
      } else {
        setUserId(null);
      }

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        const user = session?.user;
        if (user) setUserId(user.id);
        else setUserId(null);
      });

      unsub = () => subscription.unsubscribe();
    })();

    return () => unsub();
  }, []);

  // Fetch Higher topics from backend
  useEffect(() => {
    const fetchHigherTopics = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/topics/higher`);
        if (!response.ok) throw new Error("Failed to fetch higher topics");
        const data = await response.json();
        setHigherTopics(data);
      } catch (err) {
        console.error("Error fetching higher topics:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchHigherTopics();
  }, []);

  // Fetch video progress
  const fetchVideoProgress = async (videoId) => {
    if (!userId) return 0;
    try {
      const response = await fetch(`${API_BASE}/api/videos/video-progress/${userId}/${videoId}`);
      const data = await response.json();
      return data.progress || 0;
    } catch (error) {
      console.error("❌ Error fetching video progress:", error);
      return 0;
    }
  };

  // Fetch signed video URL
  const fetchVideoUrl = async (filePath, videoId) => {
    if (!filePath || !userId) return;

    try {
      const response = await fetch(`${API_BASE}/api/videos/signed-url?file_path=${filePath}`);
      const data = await response.json();

      if (data.signedUrl) {
        setSelectedVideo(data.signedUrl);
        setSelectedVideoId(videoId);

        const lastProgress = await fetchVideoProgress(videoId);

        videoRef.current.addEventListener("loadedmetadata", () => {
          if (videoRef.current) {
            videoRef.current.currentTime = (lastProgress / 100) * videoRef.current.duration;
          }
        });
      }
    } catch (error) {
      console.error("❌ Error fetching signed video URL:", error);
      setSelectedVideo(null);
    }
  };

  // Update progress
  const updateWatchProgress = async () => {
    if (!videoRef.current || !selectedVideoId || !userId) return;

    const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
    const completed = progress >= 95 && videoRef.current.dataset.completed !== "true";

    if (completed) {
      videoRef.current.dataset.completed = "true";
    }

    try {
      await axios.post(`${API_BASE}/api/videos/video-progress`, {
        videoId: selectedVideoId,
        userId,
        progress,
        completed,
      });
    } catch (error) {
      console.error("❌ Failed to update watch progress:", error);
    }
  };

  const handleWatchVideo = (filePath, videoId) => {
    if (!filePath) {
      toast.error("No video available for this topic.");
      return;
    }
    fetchVideoUrl(filePath, videoId);
  };

  const closeVideo = () => {
    updateWatchProgress();
    setSelectedVideo(null);
    setSelectedVideoId(null);
  };

  const handleQuizClick = (topicId) => {
    navigate(`/quizattempts/${topicId}`);
  };

  const handleViewExamPdf = async (topicId, filePath) => {
    if (!userId) {
      toast.error("Error: User is not logged in.");
      return;
    }
    
    // Prepare window immediately (preserves user gesture context for iOS Safari)
    preparePdfWindow();
    
    try {
      const response = await axios.get(`${API_BASE}/api/topics/signed-url`, {
        params: {
          bucket_type: "topic_questions",
          file_path: encodeURIComponent(filePath),
          user_uid: userId,
          topic_id: topicId,
        },
      });

      if (response.data.signedUrl) {
        openPdfInNewTab(response.data.signedUrl);
      } else {
        toast.error("Failed to load the Exam Questions PDF.");
      }
    } catch (error) {
      console.error("❌ Error fetching exam PDF:", error);
      toast.error("Could not fetch Exam Questions.");
    }
  };

  const handleViewSolutionPdf = async (filePath) => {
    // Prepare window immediately (preserves user gesture context for iOS Safari)
    preparePdfWindow();
    
    try {
      const response = await axios.get(
        `${API_BASE}/api/topics/signed-url?bucket_type=topic_solutions&file_path=${encodeURIComponent(filePath)}`
      );
      if (response.data.signedUrl) {
        openPdfInNewTab(response.data.signedUrl);
      } else {
        toast.error("Failed to load the Solutions PDF.");
      }
    } catch (error) {
      console.error("Error fetching solution PDF:", error);
      toast.error("Could not fetch Solutions.");
    }
  };

  const toggleGrade = (grade) => {
    setCollapsedGrades(prev => ({
      ...prev,
      [grade]: !prev[grade]
    }));
  };

  const getFilteredTopics = () => {
    return higherTopics.map(grade => ({
      ...grade,
      topics: grade.topics.filter(topic =>
        topic.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    })).filter(grade => grade.topics.length > 0);
  };

  const filteredGrades = getFilteredTopics();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-teal-600 mx-auto mb-4"></div>
          <p className="text-teal-600 font-semibold">Loading Higher topics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md">
          <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-center text-red-600 font-semibold">Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Helmet>
        <title>Higher Topics | MLMaths</title>
        <link rel="canonical" href="https://mlmaths.co.uk/higher" />
        <meta name="description" content="Explore Higher Level GCSE Maths topics with videos, quizzes, and exam questions for advanced students." />
      </Helmet>

      <main className="container mx-auto py-10 px-4 sm:px-6 lg:px-12 max-w-7xl flex-grow">
        {/* Header Section */}
        <div className="text-center mb-10">
          <div className="bg-teal-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Higher Topics</h1>
          <p className="text-lg text-gray-600">Explore Higher Level GCSE Maths topics with videos, quizzes, and exam questions</p>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search Higher topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors text-gray-800"
            />
          </div>
        </div>

        {/* Topics by Grade */}
        {filteredGrades.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <p className="text-gray-600 font-medium text-lg">No topics found</p>
            <p className="text-gray-500 text-sm mt-2">Try adjusting your search query</p>
          </div>
        ) : (
          <div className="space-y-8">
            {filteredGrades.map((grade) => {
              const isCollapsed = collapsedGrades[grade.grade];
              
              return (
                <div key={grade.grade} className="bg-white rounded-2xl shadow-xl overflow-hidden border-t-4 border-teal-500">
                  {/* Grade Header - Clickable */}
                  <button
                    onClick={() => toggleGrade(grade.grade)}
                    className="w-full bg-gradient-to-r from-teal-500 to-teal-600 p-6 flex items-center justify-between hover:from-teal-600 hover:to-teal-700 transition-colors"
                  >
                    <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      {grade.grade}
                    </h2>
                    <svg 
                      className={`w-6 h-6 text-white transition-transform duration-300 ${isCollapsed ? '' : 'rotate-180'}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Topics - Collapsible */}
                  {!isCollapsed && (
                    <div className="p-6">
                      <div className="grid grid-cols-1 gap-4">
                        {grade.topics.map((topic) => (
                          <div
                            key={topic.id}
                            className="border-2 border-gray-100 rounded-xl p-6 hover:border-teal-200 hover:shadow-md transition-all"
                          >
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                              {/* Topic Name */}
                              <div className="flex-1">
                                <h3 className="text-lg font-bold text-gray-800">
                                  {topic.name}
                                </h3>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex flex-col sm:flex-row gap-3">
                                {/* Video Button */}
                                <button
                                  onClick={() => handleWatchVideo(topic.file_path, topic.id)}
                                  className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-teal-300 text-teal-600 bg-teal-50 rounded-lg hover:bg-teal-100 hover:border-teal-400 transition-colors font-medium text-sm"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Video
                                </button>

                                {/* Quiz Button */}
                                <button
                                  onClick={() => handleQuizClick(topic.id)}
                                  className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-pink-300 text-pink-600 bg-pink-50 rounded-lg hover:bg-pink-100 hover:border-pink-400 transition-colors font-medium text-sm"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                  </svg>
                                  Quiz
                                </button>

                                {/* Exam Questions Button */}
                                {topic.exam_questions_url ? (
                                  <button
                                    onClick={() => handleViewExamPdf(topic.id, topic.exam_questions_url)}
                                    className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-indigo-300 text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 hover:border-indigo-400 transition-colors font-medium text-sm"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Exam
                                  </button>
                                ) : (
                                  <div className="flex items-center justify-center px-4 py-2 border-2 border-gray-200 text-gray-400 bg-gray-50 rounded-lg font-medium text-sm">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    No Exam
                                  </div>
                                )}

                                {/* Solutions Button */}
                                {topic.solution_url ? (
                                  <button
                                    onClick={() => handleViewSolutionPdf(topic.solution_url)}
                                    className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-green-300 text-green-600 bg-green-50 rounded-lg hover:bg-green-100 hover:border-green-400 transition-colors font-medium text-sm"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Solutions
                                  </button>
                                ) : (
                                  <div className="flex items-center justify-center px-4 py-2 border-2 border-gray-200 text-gray-400 bg-gray-50 rounded-lg font-medium text-sm">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    No Solutions
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Video Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex justify-center items-center z-50 p-4"
          onClick={closeVideo}
        >
          <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={closeVideo}
              className="absolute -top-12 right-0 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full p-3 transition-all z-50 backdrop-blur-sm"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <video
              ref={videoRef}
              controls
              controlsList="nodownload"
              disablePictureInPicture
              autoPlay
              onTimeUpdate={updateWatchProgress}
              className="w-full rounded-2xl shadow-2xl"
            >
              <source src={selectedVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Higher;
